package com.steamstreet.vegasful.browser.account.subscriptions

import com.steamstreet.graphkt.client.GraphQLClientException
import com.steamstreet.vegasful.browser.account.Account
import com.steamstreet.vegasful.browser.account.GraphQL
import com.steamstreet.vegasful.browser.account.config
import com.steamstreet.vegasful.browser.account.safeShare
import com.steamstreet.vegasful.attribution.LinkCampaign
import com.steamstreet.vegasful.attribution.LinkMedium
import com.steamstreet.vegasful.attribution.LinkSource
import com.steamstreet.vegasful.attribution.LinkTags
import com.steamstreet.vegasful.attribution.newShareId
import com.steamstreet.vegasful.attribution.taggedUrl
import com.steamstreet.vegasful.browser.clientIconSvg
import com.steamstreet.vegasful.css.css
import com.steamstreet.vegasful.graphql.api.LikeInput
import com.steamstreet.vegasful.graphql.api.ShareInput
import com.steamstreet.vegasful.graphql.api.SubscriptionInput
import com.steamstreet.vegasful.web.ClientController
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.css.Display
import kotlinx.css.display
import kotlinx.css.em
import kotlinx.css.fontSize
import kotlinx.dom.addClass
import kotlinx.dom.removeClass
import kotlinx.html.classes
import kotlinx.html.dom.append
import kotlinx.html.js.div
import kotlinx.html.js.onClickFunction
import kotlinx.html.unsafe
import org.w3c.dom.Element
import org.w3c.dom.events.Event
import org.w3c.dom.events.EventTarget
import kotlin.properties.Delegates.observable

val eventHandlerContext = CoroutineScope(Dispatchers.Default)
fun EventTarget.onClick(handler: suspend (Event) -> Unit) {
    addEventListener("click", {
        eventHandlerContext.launch {
            handler(it)
        }
    })
}

const val subscribeIcon = "interaction-heart"

class EntityInteractionsController(val element: Element, private val context: CoroutineScope) : ClientController {
    private val entity = element.getAttribute("data-entity-path")
    private val entityName = element.getAttribute("data-entity-name")

    private var isLiked: Boolean? by observable(null) { _, _, new ->
        context.launch {
            updateLikeButton(new)
        }
    }

    private fun appendButton(markerClass: String, iconId: String, onClick: suspend () -> Unit) {
        element.append {
            div {
                classes = setOf(markerClass)
                css {
                    display = Display.block
                    fontSize = 1.4.em
                }
                unsafe {
                    +clientIconSvg(iconId)
                }
                onClickFunction = {
                    context.launch {
                        onClick()
                    }
                }
            }
        }
    }

    private suspend fun updateLikeButton(subscribed: Boolean?) {
        if (subscribed != null) {
            val subscribedButton = element.querySelector(".$subscribeIcon")
            if (subscribedButton != null) {
                if (subscribed) {
                    subscribedButton.addClass("liked")
                } else {
                    subscribedButton.removeClass("liked")
                }
            }
        }
    }

    private suspend fun unlike() {
        if (entity == null) return

        val oldSubscribed = isLiked
        isLiked = false
        try {
            GraphQL.mutation {
                subscriptions {
                    unsubscribe(SubscriptionInput(null, entity))
                }
            }
        } catch (t: Throwable) {
            isLiked = oldSubscribed
        }
    }

    private suspend fun like() {
        if (entity == null) return

        if (!Account.isLoggedIn()) {
            Account.login("${entity}?subscribe=1")
        }

        val oldLiked = isLiked
        isLiked = true
        try {
            GraphQL.mutation {
                subscriptions {
                    like(LikeInput(entity))
                }
            }
        } catch (t: Throwable) {
            isLiked = oldLiked
        }
    }

    private suspend fun checkLikeStatus() {
        if (entity == null) return

        if (!Account.isLoggedIn()) {
            isLiked = false
            return
        }

        try {
            GraphQL.client {
                subscriptions {
                    mine {
                        interactionSummary(entity) {
                            this.liked
                        }
                    }
                }
            }.let {
                val loggedIn = it.subscriptions.mine != null
                val interactions = it.subscriptions.mine?.interactionSummary

                isLiked = interactions?.liked == true

                if (loggedIn && isLiked != true) {
                    val shouldSubscribe = document.location?.href?.substringAfter("?")?.split("&")?.map {
                        it.split("=").let {
                            if (it.size == 1) it.first() to it.first()
                            else it[0] to it[1]
                        }
                    }?.toMap()?.get("subscribe") == "1"

                    if (shouldSubscribe) {
                        isLiked = true // assume the subscription is going to work
                        like()
                    }
                }
            }
        } catch (ce: GraphQLClientException) {
            isLiked = false
        }
    }

    suspend fun share() {
        // A fresh share id per use of the control, so a link that goes on to be forwarded stays
        // traceable to the share that started it. Nothing resolves the id back to this user yet;
        // that needs a share record written server-side, which inbound capture will want anyway.
        window.navigator.safeShare(
            url = taggedUrl(
                "https://${config.domain}${entity}",
                LinkTags(
                    source = LinkSource.WEB,
                    medium = LinkMedium.SHARE,
                    campaign = LinkCampaign.ENTITY_SHARE,
                    shareId = newShareId()
                )
            )
        )

        // Record the share as an interaction. The app already does this; without it, sharing from
        // the web contributes nothing to the user's recommendation profile.
        if (entity != null && Account.isLoggedIn()) {
            try {
                GraphQL.mutation {
                    subscriptions {
                        share(ShareInput(entity))
                    }
                }
            } catch (t: Throwable) {
                // Sharing succeeded even if recording it didn't, so there is nothing to report.
                console.warn("Unable to record share", t)
            }
        }
    }

    override suspend fun run() {
        appendButton(subscribeIcon, "heart") {
            if (isLiked == true) {
                unlike()
            } else {
                like()
            }
        }
        appendButton("interaction-share", "share") {
            share()
        }
        checkLikeStatus()
    }
}